import { useState } from 'react'
import { Plus, Bot, Search, RefreshCw, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { useToast } from '@/hooks/use-toast'
import api from '@/lib/api'
import { formatPhoneDisplay } from '@/lib/utils'
import PhoneNode from './PhoneNode'
import UnassignedZone from './UnassignedZone'
import AgentCard from './AgentCard'
import type { Assistant } from './AgentCard'

interface PhoneNumberRecord {
  id: string
  phoneNumber: string
  provider?: string
  status: string
  assistantId?: string
  outboundAssistantId?: string
  createdAt?: string
}

interface FlowCanvasProps {
  phoneNumbers: PhoneNumberRecord[]
  assistants: Assistant[]
  onRefresh: () => void
  onEdit: (assistant: Assistant) => void
  onPreview: (assistant: Assistant) => void
  onViewStats: (assistant: Assistant) => void
  onDelete: (assistant: Assistant) => void
  onClone?: (assistant: Assistant) => void
  onViewInsights?: (assistant: Assistant) => void
  onCreateAgent: () => void
}

export default function FlowCanvas({
  phoneNumbers,
  assistants,
  onRefresh,
  onEdit,
  onPreview,
  onViewStats,
  onDelete,
  onClone,
  onViewInsights,
  onCreateAgent,
}: FlowCanvasProps) {
  const { toast } = useToast()

  // Add Number dialog state
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [addMode, setAddMode] = useState<'new' | 'import'>('new')
  const [areaCode, setAreaCode] = useState('')
  const [searching, setSearching] = useState(false)
  const [foundNumber, setFoundNumber] = useState<{ phoneNumber: string; id: string } | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [importNumber, setImportNumber] = useState('')
  const [importing, setImporting] = useState(false)

  // Delete dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; phoneId: string; phoneNumber: string }>({
    open: false, phoneId: '', phoneNumber: ''
  })
  const [deleting, setDeleting] = useState(false)

  // Assignment loading states
  const [assigningInbound, setAssigningInbound] = useState<string | null>(null)
  const [assigningOutbound, setAssigningOutbound] = useState<string | null>(null)

  const inboundAgents = assistants.filter(a => a.assistantType !== 'outbound')
  const outboundAgents = assistants.filter(a => a.assistantType === 'outbound')

  // Build agent name map
  const agentNameMap: Record<string, string> = {}
  assistants.forEach(a => { agentNameMap[a.id] = a.name })

  // Determine which agents are wired to a phone (any port). Used to split
  // the canvas into per-phone groups vs. an "Unassigned Agents" zone.
  const connectedAgentIds = new Set<string>()
  phoneNumbers.forEach(p => {
    if (p.assistantId) connectedAgentIds.add(p.assistantId)
    if (p.outboundAssistantId) connectedAgentIds.add(p.outboundAssistantId)
  })

  const unassignedAgents = assistants.filter(a => !connectedAgentIds.has(a.id))
  const hasAnyConnected = assistants.some(a => connectedAgentIds.has(a.id))

  // --- Assignment Handlers ---
  const handleInboundChange = async (phoneId: string, agentId: string) => {
    setAssigningInbound(phoneId)
    try {
      if (agentId === 'none') {
        await api.post(`/api/voice/numbers/${phoneId}/unassign-assistant`)
        toast({ title: 'Unassigned', description: 'Inbound agent removed.' })
      } else {
        await api.post(`/api/voice/numbers/${phoneId}/assign-assistant`, { assistantId: agentId })
        toast({ title: 'Assigned', description: 'Inbound agent updated.' })
      }
      onRefresh()
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to update inbound agent.', variant: 'destructive' })
    } finally {
      setAssigningInbound(null)
    }
  }

  const handleOutboundChange = async (phoneId: string, agentId: string) => {
    setAssigningOutbound(phoneId)
    try {
      if (agentId === 'none') {
        await api.post(`/api/voice/numbers/${phoneId}/unassign-outbound-agent`)
        toast({ title: 'Unassigned', description: 'Outbound agent removed.' })
      } else {
        await api.post(`/api/voice/numbers/${phoneId}/assign-outbound-agent`, { assistantId: agentId })
        toast({ title: 'Assigned', description: 'Outbound agent updated.' })
      }
      onRefresh()
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to update outbound agent.', variant: 'destructive' })
    } finally {
      setAssigningOutbound(null)
    }
  }

  // --- Add Number Handlers ---
  const handleSearchNumber = async () => {
    if (areaCode && areaCode.length !== 3) {
      toast({ title: 'Invalid area code', description: 'Enter a 3-digit area code.', variant: 'destructive' })
      return
    }
    setSearching(true)
    setFoundNumber(null)
    try {
      const res = await api.post('/api/onboarding/voice/numbers/search', {
        areaCode: areaCode || undefined
      })
      if (res.data.success && res.data.phoneNumber) {
        setFoundNumber(res.data.phoneNumber)
      }
    } catch (error: any) {
      toast({ title: 'Search failed', description: error.response?.data?.error || 'Failed to find available numbers.', variant: 'destructive' })
    } finally {
      setSearching(false)
    }
  }

  const handleConfirmNumber = async () => {
    if (!foundNumber) return
    setConfirming(true)
    try {
      await api.post('/api/onboarding/voice/numbers/confirm', {
        phoneNumberId: foundNumber.id
      })
      toast({ title: 'Number added', description: `${formatPhoneDisplay(foundNumber.phoneNumber)} is now active.` })
      resetAddDialog()
      onRefresh()
    } catch (error: any) {
      toast({ title: 'Confirm failed', description: error.response?.data?.error || 'Failed to confirm the number.', variant: 'destructive' })
    } finally {
      setConfirming(false)
    }
  }

  const handleTryAnother = async () => {
    setFoundNumber(null)
    await handleSearchNumber()
  }

  const handleImportNumber = async () => {
    if (!importNumber.trim()) return
    const cleaned = importNumber.trim()
    if (!cleaned.startsWith('+') || cleaned.length < 11 || !/^\+\d{10,15}$/.test(cleaned)) {
      toast({ title: 'Invalid format', description: 'Enter a number in E.164 format (e.g. +15551234567).', variant: 'destructive' })
      return
    }
    setImporting(true)
    try {
      await api.post('/api/voice/numbers/import', { phoneNumber: cleaned })
      toast({ title: 'Number imported', description: `${formatPhoneDisplay(cleaned)} has been imported.` })
      resetAddDialog()
      onRefresh()
    } catch (error: any) {
      toast({ title: 'Import failed', description: error.response?.data?.error || 'Failed to import the number.', variant: 'destructive' })
    } finally {
      setImporting(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteConfirm.phoneId) return
    setDeleting(true)
    try {
      await api.delete(`/api/voice/numbers/${deleteConfirm.phoneId}`)
      toast({ title: 'Deleted', description: 'Phone number has been released.' })
      setDeleteConfirm({ open: false, phoneId: '', phoneNumber: '' })
      onRefresh()
    } catch (error: any) {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to delete phone number.', variant: 'destructive' })
    } finally {
      setDeleting(false)
    }
  }

  const resetAddDialog = () => {
    setShowAddDialog(false)
    setAddMode('new')
    setAreaCode('')
    setFoundNumber(null)
    setImportNumber('')
  }

  // --- Empty state ---
  if (assistants.length === 0 && phoneNumbers.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 border border-dashed border-border/60 rounded-xl">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-indigo to-brand-royal flex items-center justify-center mb-5">
          <Bot className="h-7 w-7 text-white" />
        </div>
        <h3 className="text-lg font-semibold mb-1">No Agents or Phone Numbers</h3>
        <p className="text-sm text-muted-foreground text-center max-w-sm mb-6">
          Add a phone number and create an agent to get started.
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => setShowAddDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Number
          </Button>
          <Button onClick={onCreateAgent} className="bg-gradient-to-r from-brand-indigo to-brand-royal hover:opacity-90 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Create Agent
          </Button>
        </div>

        {/* Add Number Dialog (for empty state) */}
        {renderAddNumberDialog()}
      </div>
    )
  }

  function renderAddNumberDialog() {
    return (
      <Dialog open={showAddDialog} onOpenChange={(open) => !open && resetAddDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Phone Number</DialogTitle>
            <DialogDescription>
              Get a new number or import an existing phone number.
            </DialogDescription>
          </DialogHeader>

          {/* Mode selector */}
          <div className="flex gap-2">
            <button
              className={`flex-1 text-sm px-3 py-2 rounded-md border transition-colors ${
                addMode === 'new' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => { setAddMode('new'); setFoundNumber(null); setImportNumber('') }}
            >
              Get a New Number
            </button>
            <button
              className={`flex-1 text-sm px-3 py-2 rounded-md border transition-colors ${
                addMode === 'import' ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => { setAddMode('import'); setFoundNumber(null); setAreaCode('') }}
            >
              Import Existing
            </button>
          </div>

          {addMode === 'new' ? (
              <div className="space-y-4">
                {!foundNumber ? (
                  <>
                    <div className="space-y-2">
                      <Label>Area Code (optional)</Label>
                      <Input
                        placeholder="e.g. 415"
                        value={areaCode}
                        onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                        maxLength={3}
                      />
                    </div>
                    <Button className="w-full" onClick={handleSearchNumber} disabled={searching}>
                      {searching ? (
                        <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <Search className="h-4 w-4 mr-2" />
                      )}
                      Search Available Numbers
                    </Button>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg bg-muted/50 text-center">
                      <p className="text-sm text-muted-foreground mb-1">Found number</p>
                      <p className="text-xl font-semibold">{formatPhoneDisplay(foundNumber.phoneNumber)}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" onClick={handleTryAnother} disabled={confirming || searching}>
                        {searching ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                        Try Another
                      </Button>
                      <Button className="flex-1" onClick={handleConfirmNumber} disabled={confirming}>
                        {confirming ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
                        Confirm
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Phone Number (E.164 format)</Label>
                  <Input
                    placeholder="+15551234567"
                    value={importNumber}
                    onChange={(e) => setImportNumber(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Must be an existing phone number in your account.
                  </p>
                </div>
                <Button className="w-full" onClick={handleImportNumber} disabled={importing || !importNumber.trim()}>
                  {importing ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Import Number
                </Button>
              </div>
            )
          }
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <div className="relative">
      {/* Agent count + Add buttons */}
      <div className="flex items-center justify-between mb-5 gap-2">
        <p className="text-sm text-muted-foreground whitespace-nowrap">
          {assistants.length} agent{assistants.length !== 1 ? 's' : ''} &middot; {phoneNumbers.length} number{phoneNumbers.length !== 1 ? 's' : ''}
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button variant="outline" size="sm" onClick={() => setShowAddDialog(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Number
          </Button>
          <Button size="sm" onClick={onCreateAgent}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Add Agent
          </Button>
        </div>
      </div>

      {/* Per-phone groups: each phone card sits above the agents it routes
          calls to. The phone card already shows assignment chips at its
          base ("Bob Farahan – Inb…", etc.), so a tighter visual stack reads
          as "this phone → these agents" without drawing fragile SVG lines. */}
      {phoneNumbers.length > 0 && (
        <div className="space-y-12">
          {phoneNumbers.map(phone => {
            const inboundAgent = phone.assistantId
              ? assistants.find(a => a.id === phone.assistantId)
              : undefined
            const outboundAgent = phone.outboundAssistantId
              ? assistants.find(a => a.id === phone.outboundAssistantId)
              : undefined
            // Dedupe within a single phone (same agent on both ports
            // should render once, not twice).
            const phoneAgents: Assistant[] = []
            const seen = new Set<string>()
            for (const agent of [inboundAgent, outboundAgent]) {
              if (agent && !seen.has(agent.id)) {
                phoneAgents.push(agent)
                seen.add(agent.id)
              }
            }

            return (
              <div key={phone.id} className="space-y-5">
                <div className="flex justify-center">
                  <PhoneNode
                    phone={phone}
                    inboundAgents={inboundAgents.map(a => ({ id: a.id, name: a.name }))}
                    outboundAgents={outboundAgents.map(a => ({ id: a.id, name: a.name }))}
                    assignedInboundName={phone.assistantId ? agentNameMap[phone.assistantId] : undefined}
                    assignedOutboundName={phone.outboundAssistantId ? agentNameMap[phone.outboundAssistantId] : undefined}
                    onInboundChange={handleInboundChange}
                    onOutboundChange={handleOutboundChange}
                    onDelete={(id, num) => setDeleteConfirm({ open: true, phoneId: id, phoneNumber: num })}
                    assigningInbound={assigningInbound === phone.id}
                    assigningOutbound={assigningOutbound === phone.id}
                  />
                </div>
                {phoneAgents.length > 0 && (
                  <div className={`mx-auto grid gap-5 ${
                    phoneAgents.length === 1
                      ? 'max-w-md grid-cols-1'
                      : 'max-w-3xl grid-cols-1 sm:grid-cols-2'
                  }`}>
                    {phoneAgents.map(agent => (
                      <AgentCard
                        key={agent.id}
                        assistant={agent}
                        onEdit={onEdit}
                        onPreview={onPreview}
                        onViewStats={onViewStats}
                        onDelete={onDelete}
                        onClone={onClone}
                        onViewInsights={onViewInsights}
                        voiceDisplayName={agent.voiceDisplayName}
                      />
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Unassigned Agents */}
      <UnassignedZone count={unassignedAgents.length}>
        {unassignedAgents.map(agent => (
          <AgentCard
            key={agent.id}
            assistant={agent}
            dimmed
            onEdit={onEdit}
            onPreview={onPreview}
            onViewStats={onViewStats}
            onDelete={onDelete}
            onClone={onClone}
            onViewInsights={onViewInsights}
          />
        ))}
      </UnassignedZone>

      {/* No connected agents but phones exist */}
      {!hasAnyConnected && unassignedAgents.length === 0 && phoneNumbers.length > 0 && (
        <div className="flex flex-col items-center justify-center py-16 border border-dashed border-border/60 rounded-xl">
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-brand-indigo to-brand-royal flex items-center justify-center mb-5">
            <Bot className="h-7 w-7 text-white" />
          </div>
          <h3 className="text-lg font-semibold mb-1">No AI Agents Yet</h3>
          <p className="text-sm text-muted-foreground text-center max-w-sm mb-6">
            Create your first agent to start handling calls with a personalized voice and personality.
          </p>
          <Button onClick={onCreateAgent} className="bg-gradient-to-r from-brand-indigo to-brand-royal hover:opacity-90 text-white">
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Agent
          </Button>
        </div>
      )}

      {/* Dialogs */}
      {renderAddNumberDialog()}

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, phoneId: '', phoneNumber: '' })}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Phone Number?</DialogTitle>
            <DialogDescription>
              This will permanently release <strong>{formatPhoneDisplay(deleteConfirm.phoneNumber)}</strong>. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, phoneId: '', phoneNumber: '' })} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
